import { FAILURE_TYPE_OPTIONS } from "@/lib/failedInvoiceTypes";
import { INVOICE_CATEGORIES, INVOICE_CATEGORY } from "@/lib/invoiceCategories";
import { PROCESSING_STATUS } from "@/lib/statusEnums";

/**
 * One config per queue. `source` picks the endpoint, `overview` picks the stat
 * tiles ("queue": statuses, categories and two failure types; "exceptions":
 * categories and every failure type, for the queues that fix their own status)
 * and `baseFilters` are the server-side constraints that define the queue — the old app expressed these
 * by copying a whole 650-line page per queue and hardcoding the status in the
 * query.
 */
export const INVOICE_PAGE_VARIANTS = {
  "my-invoices": {
    title: "My Invoices",
    source: "my",
    baseFilters: {},
    showStats: true,
    overview: "queue",
    showFilterButton: true,
    showColoredFilters: false,
    showQueueChips: true,
    searchPlaceholder: "Search invoices",
  },
  "invoice-manager": {
    title: "Invoices",
    source: "all",
    baseFilters: {},
    showStats: true,
    overview: "queue",
    showFilterButton: true,
    showColoredFilters: false,
    showQueueChips: true,
    searchPlaceholder: "Search invoices",
  },
  "inQuery-invoices": {
    title: "In Query Invoices",
    source: "all",
    baseFilters: { processingStatusIds: [PROCESSING_STATUS.IN_QUERY] },
    showStats: true,
    overview: "exceptions",
    showFilterButton: false,
    showColoredFilters: true,
    showQueueChips: false,
    searchPlaceholder: "Search invoices",
  },
  "failed-invoices": {
    title: "Failed Invoices",
    source: "all",
    baseFilters: { processingStatusIds: [PROCESSING_STATUS.FAILED] },
    showStats: true,
    overview: "exceptions",
    showFilterButton: false,
    showColoredFilters: true,
    showQueueChips: false,
    searchPlaceholder: "Search invoices",
  },
  "starred-invoices": {
    title: "Starred Invoices",
    source: "starred",
    baseFilters: {},
    showStats: false,
    showFilterButton: false,
    showColoredFilters: false,
    showQueueChips: false,
    searchPlaceholder: "Search invoices",
  },
};

/**
 * Queue chips are shorthand for the panel's Processing status filter. Only the
 * chip ids `OVERVIEW_STATS` actually uses ("review", "failed", "successful") are
 * reachable from the UI — the overview strip and the toolbar's queue-chip bar
 * write these statuses into the panel's filter, and `queueChipForStatuses` reads
 * the chip back from it.
 */
export const QUEUE_CHIP_FILTERS = {
  all: {},
  // The overview's "Under Review" tile filters on the processing status of
  // that name, not the review status — a different field the old "review
  // required" chip used to filter on.
  review: { processingStatusIds: [PROCESSING_STATUS.UNDER_REVIEW] },
  successful: { processingStatusIds: [PROCESSING_STATUS.SUCCESSFUL] },
  failed: { processingStatusIds: [PROCESSING_STATUS.FAILED] },
};

/**
 * Which queue chip the processing-status filter amounts to. The chips are
 * shortcuts for the panel's Processing status field, so the chip is read back
 * from it: no status is "all", exactly one status with a chip is that chip, and
 * anything else (several statuses, or one without a chip) is "mixed" — filtered,
 * but not by any single chip.
 */
export function queueChipForStatuses(processingStatusIds = []) {
  if (processingStatusIds.length === 0) return "all";
  if (processingStatusIds.length > 1) return "mixed";
  const [only] = processingStatusIds;
  const match = Object.entries(QUEUE_CHIP_FILTERS).find(
    ([, filters]) => filters.processingStatusIds?.[0] === Number(only),
  );
  return match ? match[0] : "mixed";
}

/** The category id `Samples` filters the invoice list on when clicked from
 * the overview strip. */
export const SAMPLE_INVOICE_CATEGORY_ID = INVOICE_CATEGORY.SAMPLE_INVOICE;

/** The category id `Credit Notes` filters the invoice list on — the same
 * `CategoryId` the Category chip "Credit Note" sends, so the card and the chip
 * list the same rows. */
export const CREDIT_NOTE_CATEGORY_ID = INVOICE_CATEGORY.CREDIT_NOTE;

/**
 * Columns the API can sort on, mapped to the server's `SortBy` value.
 *
 * Only these two: the old app's grid set `allowSorting` per column and defaulted
 * it to `false`, so supplier and invoice date are the only fields the backend has
 * ever been asked to sort by. Amounts are deliberately absent rather than assumed.
 */
export const SORTABLE_COLUMNS = {
  supplier: "companyName",
  invoiceDate: "invoiceDate",
};

/** Syncfusion's grid sent these words, so this is what the API parses. */
export const SORT_DIRECTIONS = { asc: "Ascending", desc: "Descending" };

export const TABLE_COLUMNS_BY_VARIANT = {
  "my-invoices": [
    "type",
    "supplier",
    "invoiceNumber",
    "invoiceDate",
    "orderNumber",
    "netAmount",
    "carriage",
    "totalAmount",
    "processingStatus",
    "assignedTo",
    "options",
  ],
  "invoice-manager": [
    "type",
    "supplier",
    "invoiceNumber",
    "invoiceDate",
    "orderNumber",
    "netAmount",
    "carriage",
    "totalAmount",
    "processingStatus",
    "assignedTo",
    "options",
  ],
  "failed-invoices": [
    "type",
    "supplier",
    "invoiceNumber",
    "invoiceDate",
    "orderNumber",
    "totalAmount",
    "processingStatus",
    "failureType",
    "assignedTo",
    "options",
  ],
  "inQuery-invoices": [
    "type",
    "supplier",
    "invoiceNumber",
    "invoiceDate",
    "orderNumber",
    "totalAmount",
    "processingStatus",
    "failureType",
    "assignedTo",
    "options",
  ],
  "starred-invoices": [
    "type",
    "supplier",
    "invoiceNumber",
    "invoiceDate",
    "orderNumber",
    "netAmount",
    "carriage",
    "totalAmount",
    "processingStatus",
    "assignedTo",
    "options",
  ],
  "approval-requests": [
    "type",
    "supplier",
    "invoiceNumber",
    "invoiceDate",
    "orderNumber",
    "netAmount",
    "carriage",
    "totalAmount",
    "assignedTo",
    "options",
  ],
};

/**
 * How much a column earns its width, so one config drives three renderings:
 * the full desktop table, the reduced tablet table, and the phone card.
 *
 * The queue's job is scan-and-act, so what survives first is what an operator
 * scans on: who it is from, which invoice, how much, what state it is in, and
 * the way to act on it. Dates and assignment are how you triage a queue you
 * already trust; PO and net are reconciliation detail you open the invoice for.
 *
 * Previously the tablet band rendered all ten columns at ~43px of content each,
 * which is not a denser table but an unreadable one.
 */
export const COLUMN_PRIORITY = {
  supplier: "essential",
  invoiceNumber: "essential",
  totalAmount: "essential",
  options: "essential",

  processingStatus: "important",
  type: "important",
  invoiceDate: "important",
  failureType: "important",
  assignedTo: "important",

  orderNumber: "optional",
  netAmount: "optional",
  carriage: "optional",
};

/**
 * A column can matter more on one queue than another. The failure type is the
 * reason the Failed and In Query queues exist, so it never drops there — the
 * generic ranking would have hidden the single field those operators triage on.
 */
const COLUMN_PRIORITY_BY_VARIANT = {
  // Both queues pin processing status in `baseFilters`, so that column reads the
  // same value on every row and carries no information here; the failure type is
  // what actually distinguishes one row from the next.
  "failed-invoices": { failureType: "essential", processingStatus: "optional" },
  "inQuery-invoices": { failureType: "essential", processingStatus: "optional" },
};

/** Which priority tiers a panel of each density can afford. */
const TIER_BUDGET = {
  cards: [],
  compact: ["essential"],
  regular: ["essential", "important"],
  full: ["essential", "important", "optional"],
};

/**
 * The columns a panel of `tier` should render for `variant`, in the variant's
 * own order. `full` is returned verbatim so the desktop table is unchanged.
 */
export function getVisibleColumns(variant, tier) {
  const columns = TABLE_COLUMNS_BY_VARIANT[variant] ?? TABLE_COLUMNS_BY_VARIANT["invoice-manager"];
  if (!tier || tier === "full") return columns;

  const budget = TIER_BUDGET[tier] ?? TIER_BUDGET.full;
  const overrides = COLUMN_PRIORITY_BY_VARIANT[variant] ?? {};

  return columns.filter((key) => {
    const priority = overrides[key] ?? COLUMN_PRIORITY[key] ?? "optional";
    return budget.includes(priority);
  });
}

export const TABLE_COLUMN_LABELS = {
  type: "",
  supplier: "Supplier",
  invoiceNumber: "Invoice Number",
  invoiceDate: "Invoice Date",
  orderNumber: "PO Number",
  netAmount: "Net",
  carriage: "Carriage",
  totalAmount: "Total",
  processingStatus: "Processing Status",
  failureType: "Failure Type",
  assignedTo: "Assigned To",
  options: "Options",
};

export const CATEGORY_FILTER_OPTIONS = INVOICE_CATEGORIES.map((category) => ({
  value: category.id,
  label: category.name,
  color: category.color,
}));

export const FAILURE_TYPE_FILTER_OPTIONS = FAILURE_TYPE_OPTIONS.map((option) => ({
  value: option.value,
  label: option.label,
  color: option.color,
}));
